import { zodResolver } from '@hookform/resolvers/zod'
import type { FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router'
import { z } from 'zod'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader
} from '@/components/ui/dialog'
import {
	AlertFillCircleIcon,
	AlertTriangleFillIcon
} from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/helpers/utils'
import { routes } from '@/routes'
import { type TKey, useTranslation } from '@/translations/i18n'
import { useLuxeFeatureFlag } from '../queries/use-get-luxe-feature-flags'
import { useGetVisualVettingScore } from '../queries/use-get-visual-vetting-score'
import { useSubmitVisualVettingListing } from '../queries/use-submit-visual-vetting-listing'
import type { ScoreLevelLabel } from '../types'
import { formatFailedCriteria, resolveDisplayScore } from '../utils'
import { FinalScoreCard } from './final-score-card'

const NOTE_MAX_LENGTH = 300

const submitConfirmationSchema = z
	.object({
		escalate: z.boolean(),
		note: z.string().max(NOTE_MAX_LENGTH)
	})
	.refine((data) => !data.escalate || data.note.trim().length > 0, {
		message: 'errors:field_required',
		path: ['note']
	})

type SubmitConfirmationForm = z.infer<typeof submitConfirmationSchema>

const dataToScoreLevel = {
	meets_standard: {
		scoreBadgeStyles: 'bg-success-100 text-success-600',
		scoreColorStyles: 'text-success-600',
		scoreLabelKeys: 'luxe_submissions:submit_confirm_meets_standards',
		descriptionKeys: 'luxe_submissions:submit_confirm_meets_description',
		placeholderKeys: 'luxe_submissions:submit_confirm_meets_placeholder',
		bannerMessageKey: null
	},
	near_miss: {
		scoreBadgeStyles: 'bg-warning-100 text-warning-600',
		scoreColorStyles: 'text-warning-600',
		scoreLabelKeys: 'luxe_submissions:submit_confirm_near_miss',
		descriptionKeys:
			'luxe_submissions:submit_confirm_not_meets_description',
		placeholderKeys: 'luxe_submissions:submit_confirm_meets_placeholder',
		bannerMessageKey: 'luxe_submissions:submit_confirm_warning_message'
	},
	significant_gap: {
		scoreBadgeStyles: 'bg-danger-100 text-danger-600',
		scoreColorStyles: 'text-danger-600',
		scoreLabelKeys: 'luxe_submissions:submit_confirm_significant_gap',
		descriptionKeys: 'luxe_submissions:submit_confirm_fail_description',
		placeholderKeys: 'luxe_submissions:submit_confirm_fail_placeholder',
		bannerMessageKey:
			'luxe_submissions:submit_confirm_significant_gap_message'
	},
	not_qualified: {
		scoreBadgeStyles: 'bg-danger-100 text-danger-600',
		scoreColorStyles: 'text-danger-600',
		scoreLabelKeys: 'luxe_submissions:submit_confirm_not_qualified',
		descriptionKeys: 'luxe_submissions:submit_confirm_fail_description',
		placeholderKeys:
			'luxe_submissions:submit_confirm_not_qualified_placeholder',
		bannerMessageKey:
			'luxe_submissions:submit_confirm_not_qualified_message'
	}
} as const satisfies Record<
	ScoreLevelLabel,
	{
		scoreBadgeStyles: string
		scoreColorStyles: string
		scoreLabelKeys: TKey
		descriptionKeys: TKey
		placeholderKeys: TKey
		bannerMessageKey: TKey | null
	}
>

const SubmitConfirmationSkeleton = () => (
	<div className='flex flex-col gap-4'>
		<Skeleton height={20} width={200} />
		<Skeleton height={14} width='100%' />
		<div className='flex items-start justify-between rounded-lg bg-neutral-100 p-4'>
			<div className='flex flex-col gap-1'>
				<Skeleton height={12} width={80} />
				<Skeleton height={32} width={60} />
			</div>
			<Skeleton height={24} width={100} />
		</div>
		<div className='flex items-start gap-3'>
			<Skeleton height={20} width={36} />
			<div className='flex flex-col gap-1'>
				<Skeleton height={14} width={150} />
				<Skeleton height={14} width={250} />
			</div>
		</div>
		<div className='flex flex-col gap-2'>
			<Skeleton height={14} width={120} />
			<Skeleton height={160} width='100%' />
		</div>
	</div>
)

const WarningBanner = ({
	message,
	show
}: {
	message: string | null
	show: boolean
}) => {
	if (!show) {
		return null
	}
	return (
		<div className='flex items-center gap-3 bg-neutral-100 p-4'>
			<AlertTriangleFillIcon
				className='mt-0.5 shrink-0 text-orange-600'
				size={20}
			/>
			<p className='font-semibold text-grey-1000 text-sm'>{message}</p>
		</div>
	)
}

const ErrorBanner = ({
	messages,
	show
}: {
	messages: string[] | null
	show: boolean
}) => {
	if (!(show && messages?.length)) {
		return null
	}
	return (
		<>
			{messages.map((message) => (
				<div
					className='flex items-center gap-3 bg-neutral-100 p-4'
					key={message}
				>
					<AlertFillCircleIcon
						className='mt-0.5 shrink-0 text-red-700'
						size={20}
					/>
					<p className='font-semibold text-grey-1000 text-sm'>
						{message}
					</p>
				</div>
			))}
		</>
	)
}

type TranslateFn = ReturnType<typeof useTranslation>['t']

interface BannerInput {
	hasBlockerFailure: boolean
	hasTableStakeFailure: boolean
	level: ScoreLevelLabel
	score: number
	tsStakesText: string
	failedCount: number
}

// Near miss - 60-74 score; has Table Stake failure + score also fails; has Table Stake + meets
const shouldShowWarningBanner = ({
	level,
	hasTableStakeFailure
}: BannerInput): boolean =>
	(level === 'near_miss' && !hasTableStakeFailure) ||
	(hasTableStakeFailure &&
		(level === 'meets_standard' || level === 'significant_gap'))

// Error banner: blocker failure with passing score, significant_gap/not_qualified (no TS), multiple TS failures
const shouldShowErrorBanner = ({
	hasBlockerFailure,
	hasTableStakeFailure,
	level
}: BannerInput): boolean =>
	hasBlockerFailure ||
	hasTableStakeFailure ||
	(!hasTableStakeFailure &&
		(level === 'significant_gap' || level === 'not_qualified'))

const getErrorBannerMessage = (
	t: TranslateFn,
	{ hasBlockerFailure, hasTableStakeFailure, level }: BannerInput
): string[] | null => {
	if (hasBlockerFailure || hasTableStakeFailure) {
		const messages: string[] = []
		if (hasBlockerFailure) {
			messages.push(
				t('luxe_submissions:submit_confirm_blocker_failure_message')
			)
		}
		if (hasTableStakeFailure) {
			messages.push(
				t(
					'luxe_submissions:submit_confirm_table_stakes_failure_message'
				)
			)
		}
		return messages
	}

	if (level === 'significant_gap') {
		return [t('luxe_submissions:submit_confirm_significant_gap_message')]
	}
	if (level === 'not_qualified') {
		return [t('luxe_submissions:submit_confirm_not_qualified_message')]
	}

	return null
}

const getWarningBannerMessage = (
	t: TranslateFn,
	{ hasTableStakeFailure, level, tsStakesText, failedCount }: BannerInput
): string | null => {
	if (hasTableStakeFailure) {
		return t(
			level === 'meets_standard'
				? 'luxe_submissions:submit_confirm_ts_failure_score_passes'
				: 'luxe_submissions:submit_confirm_ts_failure_score_fails',
			{
				stakes: tsStakesText,
				count: failedCount
			}
		)
	}

	if (level === 'near_miss') {
		return t('luxe_submissions:submit_confirm_warning_message')
	}

	return null
}

interface SubmitConfirmationDialogProps {
	listingId: string
	open: boolean
	onOpenChange: (open: boolean) => void
}

export const SubmitConfirmationDialog: FC<SubmitConfirmationDialogProps> = ({
	listingId,
	open,
	onOpenChange
}) => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const fromSearch = location.state?.fromSearch

	const {
		handleSubmit,
		watch,
		control,
		register,
		clearErrors,
		formState: { errors }
	} = useForm({
		resolver: zodResolver(submitConfirmationSchema),
		defaultValues: {
			escalate: false,
			note: ''
		}
	})

	const escalate = watch('escalate')
	const note = watch('note')

	const { data: scoreResponse, isLoading: isScoreLoading } =
		useGetVisualVettingScore(listingId, open)

	const { isEnabled: isFinalScoreEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-final-score-display'
	)
	const submitMutation = useSubmitVisualVettingListing(listingId, {
		onSuccess: () => {
			onOpenChange(false)
			navigate({
				pathname:
					routes.LUXE_SUBMISSIONS_QUEUE_VISUAL_VETTING.handle
						.fullPath,
				search: fromSearch
			})
		}
	})

	const designScore =
		scoreResponse?.score == null ? null : Math.floor(scoreResponse.score)
	const score =
		resolveDisplayScore({
			designScore: scoreResponse?.score ?? null,
			finalScore: scoreResponse?.finalScore,
			isFinalScoreEnabled
		}) ?? 0
	const level = scoreResponse?.label ?? 'not_qualified'
	const hasTableStakeFailure = scoreResponse?.hasTableStakeFailure ?? false
	const hasBlockerFailure = scoreResponse?.hasBlockerFailure ?? false
	const failedTableStakes = scoreResponse?.failedTableStakes ?? []
	const failedBlockers = scoreResponse?.failedBlockers ?? []
	const blockersFailedCount = scoreResponse?.blockersFailedCount ?? 0

	const onSubmit = handleSubmit((data: SubmitConfirmationForm) => {
		submitMutation.mutate({
			isEscalated: data.escalate,
			note: data.note ? data.note : undefined
		})
	})

	const levelData = dataToScoreLevel[level]

	const tsStakesText = formatFailedCriteria(failedTableStakes, t)

	const bannerInput: BannerInput = {
		hasBlockerFailure,
		hasTableStakeFailure,
		level,
		score,
		tsStakesText,
		failedCount: failedTableStakes.length
	}

	const showWarningBanner = shouldShowWarningBanner(bannerInput)
	const showErrorBanner = shouldShowErrorBanner(bannerInput)
	const warningBannerMessage = getWarningBannerMessage(t, bannerInput)
	const errorBannerMessage = getErrorBannerMessage(t, bannerInput)

	return (
		<Dialog onOpenChange={onOpenChange} open={open}>
			<DialogContent
				className='flex max-w-120 flex-col overflow-hidden'
				showCloseButton={false}
			>
				<DialogHeader>
					{t('luxe_submissions:submit_confirm_title')}
				</DialogHeader>

				<DialogBody className='flex-1 gap-6 overflow-y-auto'>
					{isScoreLoading ? (
						<SubmitConfirmationSkeleton />
					) : (
						<div className='flex flex-col gap-4'>
							<h3 className='font-bold text-xl'>
								{t(
									'luxe_submissions:submit_confirm_review_title'
								)}
							</h3>
							{!isFinalScoreEnabled && (
								<p className='text-base text-neutral-700'>
									{t(levelData.descriptionKeys)}
								</p>
							)}

							{isFinalScoreEnabled ? (
								<FinalScoreCard
									blockersFailedCount={blockersFailedCount}
									designScore={designScore}
									failedBlockers={failedBlockers}
									score={score}
								/>
							) : (
								<>
									{/* Score card */}
									<div className='flex items-start justify-between rounded-lg bg-neutral-100 p-4'>
										<div>
											<p className='font-semibold text-neutral-700 text-xs uppercase'>
												{t(
													'luxe_submissions:submit_confirm_score_label'
												)}
											</p>
											<p
												className={cn(
													'font-bold text-display-xs-fixed',
													levelData.scoreColorStyles
												)}
											>
												{score}
												<span className='font-normal text-neutral-700 text-xs'>
													/100
												</span>
											</p>
										</div>
										{!(
											level === 'meets_standard' &&
											hasTableStakeFailure
										) && (
											<span
												className={cn(
													'flex min-w-25 items-center justify-center rounded-md px-1 py-0.5 font-medium text-sm',
													levelData.scoreBadgeStyles
												)}
											>
												{t(levelData.scoreLabelKeys)}
											</span>
										)}
									</div>

									<WarningBanner
										message={warningBannerMessage}
										show={showWarningBanner}
									/>

									<ErrorBanner
										messages={errorBannerMessage}
										show={showErrorBanner}
									/>
								</>
							)}

							{/* Escalate toggle */}
							<div className='flex flex-col gap-2'>
								<div className='flex items-start gap-3'>
									<Controller
										control={control}
										name='escalate'
										render={({ field }) => (
											<Switch
												checked={field.value}
												onChange={(v) => {
													field.onChange(v)
													if (errors.note?.message) {
														clearErrors('note')
													}
												}}
											/>
										)}
									/>

									<div className='flex flex-col'>
										<span className='font-semibold text-neutral-700 text-sm'>
											{t(
												'luxe_submissions:submit_confirm_escalate_title'
											)}
										</span>
										<span className='text-neutral-600 text-sm'>
											{t(
												'luxe_submissions:submit_confirm_escalate_description'
											)}
										</span>
									</div>
								</div>
								{errors.escalate?.message && (
									<InputHelpText
										error={t(
											errors.escalate.message as TKey
										)}
									/>
								)}
							</div>

							{/* Note */}
							<div className='flex flex-col gap-2'>
								<p className='font-semibold text-neutral-700 text-sm'>
									{t(
										'luxe_submissions:submit_confirm_add_note'
									)}
									{!escalate && ` (${t('common:optional')})`}
								</p>
								<Textarea
									aria-invalid={!!errors.note}
									className='min-h-32'
									maxLength={NOTE_MAX_LENGTH}
									placeholder={t(levelData.placeholderKeys)}
									rows={10}
									{...register('note')}
								/>
								{errors.note?.message ? (
									<InputHelpText
										error={t(errors.note.message as TKey)}
									/>
								) : (
									<span className='text-neutral-400 text-sm'>
										{note.length}/{NOTE_MAX_LENGTH}
									</span>
								)}
							</div>
						</div>
					)}
				</DialogBody>

				<DialogFooter className='flex flex-row justify-between bg-neutral-white'>
					<DialogClose
						render={
							<ButtonLegacy type='button' variant='tertiary' />
						}
					>
						{t('common:close')}
					</DialogClose>
					<ButtonLegacy
						disabled={submitMutation.isPending}
						onClick={onSubmit}
						type='button'
						variant='brand'
					>
						{t('luxe_submissions:submit_listing')}
						{submitMutation.isPending && <Spinner />}
					</ButtonLegacy>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
